/**
 * useTaxonomyLookup - Hook to lookup taxonomy term names from IDs
 * Builds Maps for fast ID→name lookups in table displays
 */

import { useMemo } from 'react';
import { useTaxonomies, type TaxonomyTerm } from './useTaxonomies';

interface TaxonomyLookup {
  categories: Map<number, string>;
  tags: Map<number, string>;
  isLoading: boolean;
  getCategoryName: (id: number) => string;
  getTagName: (id: number) => string;
}

export function useTaxonomyLookup(siteId: string | undefined): TaxonomyLookup {
  const { categories, tags, isLoading } = useTaxonomies(siteId);

  // Build category ID→name map
  const categoryMap = useMemo(() => {
    const map = new Map<number, string>();
    categories.forEach((term: TaxonomyTerm) => {
      const id = parseInt(term.value, 10);
      if (!isNaN(id)) {
        map.set(id, term.label);
      }
    });
    return map;
  }, [categories]);

  // Build tag ID→name map
  const tagMap = useMemo(() => {
    const map = new Map<number, string>();
    tags.forEach((term: TaxonomyTerm) => {
      const id = parseInt(term.value, 10);
      if (!isNaN(id)) {
        map.set(id, term.label);
      }
    });
    return map;
  }, [tags]);

  // Helper functions
  const getCategoryName = (id: number): string => {
    return categoryMap.get(id) || `Category #${id}`;
  };

  const getTagName = (id: number): string => {
    return tagMap.get(id) || `Tag #${id}`;
  };

  return {
    categories: categoryMap,
    tags: tagMap,
    isLoading,
    getCategoryName,
    getTagName,
  };
}
